// src/pages/admin/tabs/parts/PostForm.jsx
import React from 'react';
import styled from 'styled-components';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import { normalizeMediaUrl } from '../../../../api/base';
import { Row2, Field, Actions, Btn, YesNoToggle } from '../../ui';
import MediaPickerModal from '../../components/MediaPickerModal';

const Preview = styled.div`
  margin-top:8px;
  img{ max-width: 280px; border-radius:12px; border:1px solid var(--bdr); display:block; }
`;

function nonEmpty(s){ return s != null && String(s).trim() !== ''; }
const asBool = (x, def=false) => (typeof x === 'boolean' ? x :
  (x === 1 || x === '1' || x === 'true') ? true :
  (x === 0 || x === '0' || x === 'false') ? false : !!def);

// Функция для форматирования ошибок
const formatError = (error) => {
  if (!error) return 'Неизвестная ошибка';
  
  // Если есть детали ошибки (валидационные ошибки)
  if (error.payload?.details) {
    const details = error.payload.details;
    if (typeof details === 'string') return details;
    
    // Если это объект с полями ошибок
    if (typeof details === 'object') {
      const messages = [];
      for (const [field, errors] of Object.entries(details)) {
        if (Array.isArray(errors)) {
          messages.push(`${field}: ${errors.join(', ')}`);
        } else {
          messages.push(`${field}: ${errors}`);
        }
      }
      return messages.length > 0 ? messages.join('; ') : 'Ошибка валидации';
    }
  }
  
  // Обычное сообщение об ошибке
  return error.message || 'Ошибка сохранения';
};

export default function PostForm({ editing, onSaved, onCancel }){
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  const [pickerOpen, setPickerOpen] = React.useState(false);

  // NEW: состояние и ref для загрузки обложки из файла
  const [coverBusy, setCoverBusy] = React.useState(false);
  const coverInputRef = React.useRef(null);

  const [form, setForm] = React.useState(()=>{
    const p = editing || {};
    return {
      section: p.section || 'blog',
      slug: p.slug || '',
      title_ru: p.title_ru || '',
      title_en: p.title_en || '',
      title_zh: p.title_zh || '',
      excerpt_ru: p.excerpt_ru || '',
      excerpt_en: p.excerpt_en || '',
      excerpt_zh: p.excerpt_zh || '',
      content_ru: p.content_ru || '',
      content_en: p.content_en || '',
      content_zh: p.content_zh || '',

      // обложка (можно выбрать из медиатеки или загрузить файл)
      featured_image: p.featured_image || p.cover || '',
      featured_media_id: p.featured_media_id || undefined,

      author: p.author || '',
      tags: p.tags || '',
      
      // дата публикации (для СМИ)
      published_at: p.published_at ? p.published_at.split('T')[0] : '',

      // тянем реальное состояние и показываем в UI
      is_published: asBool(p.is_published, false),
      is_featured: asBool(p.is_featured, false),
      is_active: asBool(p.is_active, true),

      meta_title_ru: p.meta_title_ru || '',
      meta_title_en: p.meta_title_en || '',
      meta_title_zh: p.meta_title_zh || '',
      meta_description_ru: p.meta_description_ru || '',
      meta_description_en: p.meta_description_en || '',
      meta_description_zh: p.meta_description_zh || '',
    };
  });

  // при смене editing — подтянуть актуальные значения
  React.useEffect(()=>{
    if (!editing) return;
    setForm(f=>({
      ...f,
      section: editing.section || 'blog',
      slug: editing.slug || '',
      title_ru: editing.title_ru || '',
      title_en: editing.title_en || '',
      title_zh: editing.title_zh || '',
      excerpt_ru: editing.excerpt_ru || '',
      excerpt_en: editing.excerpt_en || '',
      excerpt_zh: editing.excerpt_zh || '',
      content_ru: editing.content_ru || '',
      content_en: editing.content_en || '',
      content_zh: editing.content_zh || '',

      featured_image: editing.featured_image || editing.cover || '',
      featured_media_id: editing.featured_media_id || undefined,

      author: editing.author || '',
      tags: editing.tags || '',
      
      published_at: editing.published_at ? editing.published_at.split('T')[0] : '',

      is_published: asBool(editing.is_published, false),
      is_featured: asBool(editing.is_featured, false),
      is_active: asBool(editing.is_active, true),

      meta_title_ru: editing.meta_title_ru || '',
      meta_title_en: editing.meta_title_en || '',
      meta_title_zh: editing.meta_title_zh || '',
      meta_description_ru: editing.meta_description_ru || '',
      meta_description_en: editing.meta_description_en || '',
      meta_description_zh: editing.meta_description_zh || '',
    }));
  }, [editing]);

  const patch = (k,v)=> setForm(f=>({ ...f, [k]: v }));

  const submit = async ()=>{
    setErr(''); setOk(''); setSaving(true);
    try{
      if (!nonEmpty(form.title_ru) && !nonEmpty(form.title_en)) throw new Error('Укажите заголовок RU или EN');
      if (!nonEmpty(form.content_ru) && !nonEmpty(form.content_en)) throw new Error('Укажите контент RU или EN');

      const payload = {
        section: form.section,
        title_ru: nonEmpty(form.title_ru) ? form.title_ru : undefined,
        title_en: nonEmpty(form.title_en) ? form.title_en : undefined,
        title_zh: form.title_zh || undefined,
        excerpt_ru: form.excerpt_ru || undefined,
        excerpt_en: form.excerpt_en || undefined,
        excerpt_zh: form.excerpt_zh || undefined,
        content_ru: nonEmpty(form.content_ru) ? form.content_ru : undefined,
        content_en: nonEmpty(form.content_en) ? form.content_en : undefined,
        content_zh: form.content_zh || undefined,

        featured_image: form.featured_image || undefined,
        featured_media_id: form.featured_media_id ?? undefined,

        author: form.author || undefined,
        tags: form.tags || undefined,
        
        // дата публикации (если указана)
        published_at: form.published_at || undefined,

        // точно буль, без undefined
        is_published: !!form.is_published,
        is_featured: !!form.is_featured,
        is_active: !!form.is_active,

        meta_title_ru: form.meta_title_ru || undefined,
        meta_title_en: form.meta_title_en || undefined,
        meta_title_zh: form.meta_title_zh || undefined,
        meta_description_ru: form.meta_description_ru || undefined,
        meta_description_en: form.meta_description_en || undefined,
        meta_description_zh: form.meta_description_zh || undefined,
      };

      if (editing?.id) {
        await AdminAPI.updatePost(editing.id, payload);
      } else {
        await AdminAPI.createPost(payload); // slug генерируется на сервере
      }
      setOk('Сохранено');
      onSaved && onSaved(form.section); // передаем секцию для переключения
    } catch(e){
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  // выбрать обложку из медиатеки (single)
  const onPickCover = (selected)=>{
    if (!selected?.length) return;
    const file = selected[0];
    const url = normalizeMediaUrl(file.thumbnail_url || file.url);
    patch('featured_media_id', file.id);
    patch('featured_image', url || '');
    // очистим file input, чтобы можно было заново выбрать тот же файл
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  // загрузить обложку с диска
  const uploadCover = async (file) => {
    if (!file) return;
    setErr('');
    setCoverBusy(true);
    try {
      const up = await AdminAPI.uploadMedia(file);
      const media = up?.file || up || {};
      const url = normalizeMediaUrl(media.thumbnail_url || media.url || '');

      if (!media.id || !url) throw new Error('Сервер не вернул id или url');

      patch('featured_media_id', media.id);
      patch('featured_image', url);
      setOk('Обложка загружена');
    } catch (e) {
      setErr(e?.message || 'Не удалось загрузить обложку');
    } finally {
      setCoverBusy(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const clearCover = ()=>{
    patch('featured_media_id', undefined);
    patch('featured_image', '');
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  return (
    <div>
      {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
      {ok && <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

      <Row2>
        <Field>Секция
          <select value={form.section} onChange={e=>patch('section', e.target.value)}>
            <option value="blog">blog</option>
            <option value="news">news</option>
            <option value="media">media</option>
            <option value="about">about</option>
          </select>
        </Field>
        {editing?.slug && (
          <Field>Slug (генерируется сервером)
            <input value={form.slug} disabled readOnly />
          </Field>
        )}
      </Row2>

      <Row2>
        <Field>Заголовок RU
          <input value={form.title_ru} onChange={e=>patch('title_ru', e.target.value)} />
        </Field>
        <Field>Заголовок EN
          <input value={form.title_en} onChange={e=>patch('title_en', e.target.value)} />
        </Field>
      </Row2>

      <Field>Заголовок ZH (中文)
        <input value={form.title_zh} onChange={e=>patch('title_zh', e.target.value)} />
      </Field>

      <Row2>
        <Field>Анонс RU
          <textarea rows={3} value={form.excerpt_ru} onChange={e=>patch('excerpt_ru', e.target.value)} />
        </Field>
        <Field>Анонс EN
          <textarea rows={3} value={form.excerpt_en} onChange={e=>patch('excerpt_en', e.target.value)} />
        </Field>
      </Row2>

      <Field>Анонс ZH (中文)
        <textarea rows={3} value={form.excerpt_zh} onChange={e=>patch('excerpt_zh', e.target.value)} />
      </Field>

      <Row2>
        <Field>Контент RU (HTML)
          <textarea rows={8} value={form.content_ru} onChange={e=>patch('content_ru', e.target.value)} placeholder="<p>Текст...</p>" />
        </Field>
        <Field>Контент EN (HTML)
          <textarea rows={8} value={form.content_en} onChange={e=>patch('content_en', e.target.value)} placeholder="<p>Text...</p>" />
        </Field>
      </Row2>

      <Field>Контент ZH (HTML, 中文)
        <textarea rows={8} value={form.content_zh} onChange={e=>patch('content_zh', e.target.value)} placeholder="<p>文本...</p>" />
      </Field>

      <Row2>
        <Field>Автор
          <input value={form.author} onChange={e=>patch('author', e.target.value)} />
        </Field>
        <Field>Теги (через запятую)
          <input value={form.tags} onChange={e=>patch('tags', e.target.value)} placeholder="media, analytics" />
        </Field>
      </Row2>

      {/* Дата публикации - показываем только для СМИ (media) */}
      {form.section === 'media' && (
        <Row2>
          <Field>Дата новости
            <input 
              type="date" 
              value={form.published_at} 
              onChange={e=>patch('published_at', e.target.value)} 
            />
            <small>Дата публикации новости (опционально)</small>
          </Field>
        </Row2>
      )}

      {/* Обложка: загрузить файл ИЛИ выбрать из медиатеки */}
      <Row2>
        <Field>Обложка
          <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
            {/* скрытый input для загрузки */}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              style={{ display:'none' }}
              onChange={(e)=>{
                const file = e.target.files?.[0];
                if (file) uploadCover(file);
              }}
            />
            <Btn 
              type="button"
              onClick={(e)=>{
                e.preventDefault();
                e.stopPropagation();
                if (coverInputRef.current) {
                  coverInputRef.current.click();
                }
              }} 
              disabled={coverBusy}
            >
              {coverBusy ? 'Загрузка…' : 'Загрузить файл'}
            </Btn>

            {/* выбор из медиатеки (single) */}
            <Btn 
              type="button"
              onClick={(e)=>{
                e.preventDefault();
                setPickerOpen(true);
              }} 
              disabled={coverBusy}
            >
              Выбрать из медиатеки
            </Btn>

            {form.featured_image && (
              <Btn 
                type="button"
                onClick={(e)=>{
                  e.preventDefault();
                  clearCover();
                }} 
                disabled={coverBusy}
              >
                Убрать
              </Btn>
            )}
          </div>
          {form.featured_image && (
            <Preview><img src={normalizeMediaUrl(form.featured_image)} alt="cover" /></Preview>
          )}
          {form.featured_media_id && (
            <div style={{marginTop:6, fontSize:12, opacity:.75}}>
              media_id: <b>{form.featured_media_id}</b>
            </div>
          )}
        </Field>
      </Row2>

      {/* Да/Нет — показываем актуальное и меняем по клику */}
      <Row2>
        <Field>Опубликовано
          <YesNoToggle value={form.is_published} onChange={(v)=>patch('is_published', v)} />
        </Field>

        <Field>Активно
          <YesNoToggle value={form.is_active} onChange={(v)=>patch('is_active', v)} />
        </Field>

        <Field>Избранное
          <YesNoToggle value={form.is_featured} onChange={(v)=>patch('is_featured', v)} />
        </Field>
      </Row2>

      <Row2>
        <Field>Meta title RU
          <input value={form.meta_title_ru} onChange={e=>patch('meta_title_ru', e.target.value)} />
        </Field>
        <Field>Meta title EN
          <input value={form.meta_title_en} onChange={e=>patch('meta_title_en', e.target.value)} />
        </Field>
      </Row2>

      <Field>Meta title ZH (中文)
        <input value={form.meta_title_zh} onChange={e=>patch('meta_title_zh', e.target.value)} />
      </Field>

      <Row2>
        <Field>Meta description RU
          <textarea rows={3} value={form.meta_description_ru} onChange={e=>patch('meta_description_ru', e.target.value)} />
        </Field>
        <Field>Meta description EN
          <textarea rows={3} value={form.meta_description_en} onChange={e=>patch('meta_description_en', e.target.value)} />
        </Field>
      </Row2>

      <Field>Meta description ZH (中文)
        <textarea rows={3} value={form.meta_description_zh} onChange={e=>patch('meta_description_zh', e.target.value)} />
      </Field>

      <Actions style={{marginTop:10}}>
        <Btn $kind="primary" onClick={submit} disabled={saving}>
          {saving ? 'Сохранение…' : (editing?.id ? 'Сохранить' : 'Создать')}
        </Btn>
        {onCancel && <Btn onClick={onCancel}>Отмена</Btn>}
      </Actions>

      {/* модалка медиатеки */}
      <MediaPickerModal
        open={pickerOpen}
        onClose={()=>setPickerOpen(false)}
        onPick={onPickCover}
        multi={false}
      />
    </div>
  );
}
